import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISSED_KEY = 'posterfire_email_banner_dismissed';
const SUPPRESSED_PATHS = ['/checkout', '/cart', '/aaa'];

const EmailSignupBanner = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const wasDismissed = localStorage.getItem(DISMISSED_KEY);
    const wasSubscribed = localStorage.getItem('posterfire_subscribed');
    if (wasDismissed || wasSubscribed) return;
    if (SUPPRESSED_PATHS.some(p => location.pathname.startsWith(p))) return;

    axios.get(`${API}/popup-banner`).then(res => {
      const s = res.data;
      if (!s.enabled) return;
      setSettings(s);
      const timer = setTimeout(() => setDismissed(false), (s.delay_seconds || 2) * 1000);
      return () => clearTimeout(timer);
    }).catch(() => {});
  }, [location.pathname]);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/email-subscribe`, { email, source: 'discount_banner' });
      setSubscribed(true);
      localStorage.setItem('posterfire_subscribed', 'true');
      toast.success('You\'re in! Check your email for your discount.');
      setTimeout(() => { setDismissed(true); }, 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong';
      if (msg === 'Already subscribed') {
        toast.info('You\'re already subscribed!');
        localStorage.setItem('posterfire_subscribed', 'true');
        setTimeout(() => { setDismissed(true); }, 2000);
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  if (dismissed || !settings) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4" data-testid="email-signup-banner">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={handleDismiss} />
      <div className="relative w-full max-w-md bg-gray-950 border border-gray-800 rounded-2xl overflow-hidden shadow-2xl shadow-orange-500/10 animate-slide-up">
        <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-amber-400" />
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-white transition-colors rounded-full hover:bg-gray-800"
          data-testid="email-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="px-6 pt-8 pb-6">
          {!subscribed ? (
            <>
              <div className="flex items-center justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                  <Mail className="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 className="text-center text-xl font-black text-white tracking-tight mb-1">
                {settings.headline} <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">{settings.discount}</span>
              </h3>
              <p className="text-center text-gray-400 text-sm mb-5">
                {settings.subtext}
              </p>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="email-signup-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-sm rounded-lg hover:from-red-600 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/20 flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="email-signup-submit"
                >
                  {loading ? '...' : <><span>JOIN</span><ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
              <p className="text-center text-gray-600 text-[10px] mt-3">
                No spam. Unsubscribe anytime. Your data is never shared.
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-xl font-black text-white mb-1">You're In!</h3>
              <p className="text-gray-400 text-sm">Check your inbox for your discount code.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailSignupBanner;
